import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const ResetPasswordPage = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            if (!token) {
                setError('Token non fornito.');
                setIsLoading(false);
                return;
            }
            try {
                await api.get(`/api/users/checkRecoverPassword/${token}`);
                setIsTokenValid(true);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Questo link non è valido o è scaduto. Richiedi un nuovo link di recupero.');
            } finally {
                setIsLoading(false);
            }
        };
        checkToken();
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Le password non corrispondono.');
            return;
        }
        if (password.length < 8) {
            setError('La password deve essere di almeno 8 caratteri.');
            return;
        }
        setError('');
        setIsLoading(true);

        try {
            await api.post('/api/users/saveNewPassword', { newPassword: password, code: token });
            setMessage('Password cambiata con successo! Ora puoi accedere con le tue nuove credenziali.');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Si è verificato un errore. Riprova.');
        } finally {
            setIsLoading(false);
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return <p className="text-center text-slate-300">Verifica in corso...</p>;
        }

        if (message) {
            return (
                <div className="text-center">
                    <p className="text-green-400 mb-6">{message}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full bg-sky-600 hover:bg-sky-500 text-white font-bold py-3 px-4 rounded-lg inline-block transition-colors"
                    >
                        Vai al Login
                    </button>
                </div>
            );
        }

        if (!isTokenValid) {
            return (
                <div className="text-center">
                    <p className="text-red-400 mb-6">{error || 'Si è verificato un errore.'}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="font-medium text-sky-400 hover:text-sky-300 transition-colors"
                    >
                        Torna alla pagina principale
                    </button>
                </div>
            );
        }

        return (
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">Nuova Password</label>
                    <input
                        id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••" required
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                </div>
                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">Conferma Nuova Password</label>
                    <input
                        id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="••••••••" required
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                </div>
                {error && (
                    <div className="text-red-400 text-sm text-center">{error}</div>
                )}
                <div>
                    <button type="submit" disabled={isLoading} className="w-full bg-sky-600 hover:bg-sky-500 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 disabled:bg-slate-600">
                        {isLoading ? 'Salvataggio...' : 'Salva Nuova Password'}
                    </button>
                </div>
                <div className="text-center mt-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="font-medium text-sky-400 hover:text-sky-300 transition-colors"
                    >
                        &larr; Torna indietro
                    </button>
                </div>
            </form>
        );
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-block p-3 rounded-lg bg-sky-500 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Crea una nuova password</h1>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 shadow-lg">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;